import type { FeatureModule, FeatureState } from '../types';

/**
 * Seiten-Zoom: vergrößert die GESAMTE Seite (Layout, Bilder, Text), getrennt
 * von der reinen Schriftgröße (text-size). Umgesetzt über CSS `zoom` auf dem
 * <body>; das Widget selbst (eigener Host im Light-DOM) wird per Gegen-Zoom
 * neutralisiert, damit es nicht mitwächst. `zoom` (statt transform:scale)
 * behält den normalen Dokumentenfluss und erzeugt sauberen Scroll.
 *
 * Stufen: 110 / 125 / 150 / 175 / 200 %. Toggle startet bei 125 %; setState
 * erlaubt einen festen Prozentwert (für einen späteren Inline-Regler).
 */
const LEVELS = [110, 125, 150, 175, 200];
const DEFAULT_LEVEL = 125;

export default function createPageZoomModule(): FeatureModule {
  let enabled = false;
  let percent = DEFAULT_LEVEL;
  const STYLE_ID = 'accessify-page-zoom';
  const STORAGE_KEY = 'accessify-page-zoom';

  function apply(p: number) {
    const factor = p / 100;
    let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }
    // Body zoomt; der Widget-Host zoomt gegen (1/factor), damit das Bedienpanel
    // in Originalgröße bleibt. `zoom` multipliziert sich, also hebt 1/factor den
    // Body-Zoom exakt auf.
    styleEl.textContent =
      `body { zoom: ${factor}; }` +
      `#accessify-root, [data-accessify-root="true"] { zoom: ${1 / factor}; }`;
  }

  function clear() {
    document.getElementById(STYLE_ID)?.remove();
  }

  function activate() {
    enabled = true;
    const saved = parseInt(localStorage.getItem(STORAGE_KEY) || '', 10);
    percent = LEVELS.includes(saved) ? saved : DEFAULT_LEVEL;
    apply(percent);
    localStorage.setItem(STORAGE_KEY, String(percent));
  }

  function deactivate() {
    enabled = false;
    clear();
    localStorage.removeItem(STORAGE_KEY);
  }

  return {
    id: 'page-zoom',
    name: () => 'Page zoom',
    description: 'Enlarge the whole page (layout and images), separate from text size',
    icon: 'page-zoom',
    category: 'visual',
    activate,
    deactivate,
    getState: (): FeatureState => ({ id: 'page-zoom', enabled, value: percent }),
    setState: (state: number | { enabled?: boolean; value?: number }) => {
      if (typeof state === 'number') {
        percent = LEVELS.includes(state) ? state : DEFAULT_LEVEL;
        enabled = true;
        apply(percent);
        localStorage.setItem(STORAGE_KEY, String(percent));
        return;
      }
      if (state.value != null) {
        percent = LEVELS.includes(state.value) ? state.value : DEFAULT_LEVEL;
      }
      if (state.enabled === false) deactivate();
      else activate();
    },
  };
}
